/**
 * InfinitSales - MemberWelcomeModal
 *
 * Modal one-time que aparece no primeiro login do member criado pela empresa.
 * Comunica que sua conta é vinculada a uma Conta Empresa e que configurações
 * de plano, agente e assinatura são gerenciadas pelo administrador.
 *
 * Visibilidade:
 * - Mostra quando: user.created_by_company === true E flag em localStorage
 *   `member-welcome-seen-{user.id}` não está marcada.
 * - Click "Entendi" → marca flag, modal nunca mais aparece nesse browser.
 *
 * O modal é montado globalmente em App.js (dentro do AuthProvider) para que
 * funcione em qualquer rota que o member abrir após login.
 *
 * Compliance: o texto deixa claro o escopo de acesso do admin (leitura de
 * contatos/conversas/métricas via /empresa). Atende ao item de Privacy
 * Policy 10-A.3 ("Informação ao Membro").
 */
import React, { useEffect, useState } from 'react';
import { Building2, Check, X } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import companyService from '../services/global/companyService';

const seenKey = (userId) => `member-welcome-seen-${userId}`;

const MemberWelcomeModal = () => {
  const { colors } = useTheme();
  const { user, isCompanyMember } = useAuth();
  const [open, setOpen] = useState(false);
  const [companyName, setCompanyName] = useState(null);

  useEffect(() => {
    if (!user || !isCompanyMember) {
      setOpen(false);
      return;
    }
    if (!user.created_by_company) {
      // Edge case: member that joined via another flow (unused in MVP).
      // Don't show welcome since they didn't get created by an admin.
      setOpen(false);
      return;
    }
    let seen = false;
    try {
      seen = localStorage.getItem(seenKey(user.id)) === 'true';
    } catch {}
    if (seen) {
      setOpen(false);
      return;
    }
    setOpen(true);

    // Fetch the company name to personalize the message. Best-effort: if
    // the request fails, we fall back to a generic phrase.
    companyService
      .getMyCompany()
      .then((data) => {
        if (data?.company?.name) setCompanyName(data.company.name);
      })
      .catch(() => {});
  }, [user, isCompanyMember]);

  const handleAck = () => {
    if (user?.id) {
      try {
        localStorage.setItem(seenKey(user.id), 'true');
      } catch {}
    }
    setOpen(false);
  };

  if (!open) return null;

  const styles = {
    backdrop: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.65)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2100,
      padding: '24px',
    },
    modal: {
      backgroundColor: colors.bgPrimary,
      border: `1px solid ${colors.borderColor}`,
      borderRadius: '20px',
      maxWidth: '480px',
      width: '100%',
      overflow: 'hidden',
      boxShadow: '0 16px 60px rgba(0,0,0,0.55)',
    },
    hero: {
      padding: '24px',
      background: `linear-gradient(135deg, ${colors.accentPrimary}30 0%, ${colors.accentPrimary}10 100%)`,
      borderBottom: `1px solid ${colors.borderColor}`,
      textAlign: 'center',
    },
    iconWrap: {
      width: 56,
      height: 56,
      borderRadius: 16,
      backgroundColor: colors.accentPrimary,
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 12px auto',
    },
    title: {
      fontSize: 20,
      fontWeight: 700,
      color: colors.textPrimary,
      marginBottom: 4,
    },
    subtitle: {
      fontSize: 14,
      color: colors.textSecondary,
    },
    body: {
      padding: '20px 24px',
    },
    list: {
      listStyle: 'none',
      margin: 0,
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
    },
    item: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
      fontSize: 14,
      color: colors.textPrimary,
    },
    checkIcon: {
      width: 22,
      height: 22,
      borderRadius: '50%',
      backgroundColor: `${colors.accentPrimary}25`,
      color: colors.accentPrimary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      marginTop: 1,
    },
    footer: {
      display: 'flex',
      justifyContent: 'flex-end',
      padding: '12px 24px 20px 24px',
    },
    cta: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '10px 20px',
      border: 'none',
      borderRadius: 10,
      backgroundColor: colors.accentPrimary,
      color: 'white',
      fontSize: 14,
      fontWeight: 700,
      cursor: 'pointer',
    },
  };

  const orgText = companyName
    ? <>Você foi adicionado à empresa <strong>{companyName}</strong> pelo administrador.</>
    : 'Você foi adicionado a uma Conta Empresa pelo administrador.';

  return (
    <div style={styles.backdrop}>
      <div style={styles.modal}>
        <div style={styles.hero}>
          <div style={styles.iconWrap}>
            <Building2 size={28} />
          </div>
          <div style={styles.title}>Bem-vindo!</div>
          <div style={styles.subtitle}>{orgText}</div>
        </div>
        <div style={styles.body}>
          <ul style={styles.list}>
            <li style={styles.item}>
              <span style={styles.checkIcon}><Check size={12} /></span>
              <span>
                <strong>Você opera normalmente</strong> sua inbox, contatos
                e conexão WhatsApp própria.
              </span>
            </li>
            <li style={styles.item}>
              <span style={styles.checkIcon}><Check size={12} /></span>
              <span>
                O <strong>agente IA é compartilhado</strong> com a equipe e
                gerenciado pelo administrador.
              </span>
            </li>
            <li style={styles.item}>
              <span style={styles.checkIcon}><Check size={12} /></span>
              <span>
                <strong>Plano e assinatura</strong> são da empresa. Para
                ajustes, fale com o admin.
              </span>
            </li>
            <li style={styles.item}>
              <span style={styles.checkIcon}><Check size={12} /></span>
              <span>
                O admin pode <strong>ver seus contatos e conversas</strong>
                {' '}no painel da empresa, para consolidar relatórios.
              </span>
            </li>
          </ul>
        </div>
        <div style={styles.footer}>
          <button style={styles.cta} onClick={handleAck}>
            Entendi
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default MemberWelcomeModal;
